import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  getHistorialCobranzaDetalle, reintentarEmailDetalle,
} from "../../controllers/api_billing";
import { decryptText } from "../../controllers/api";

const fmtMonto = (val) => {
  try {
    const n = Number(val);
    if (Number.isNaN(n)) return String(val);
    return n.toLocaleString("es-VE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } catch { return String(val); }
};

const fmtPeriodo = (dateStr) => {
  if (!dateStr) return "—";
  try {
    const [y, m] = dateStr.split("T")[0].split("-");
    const meses = ["", "enero", "febrero", "marzo", "abril", "mayo", "junio",
                   "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
    return `${meses[Number(m)]} ${y}`;
  } catch { return String(dateStr); }
};

export default function DetailBillingHistory() {
  const navigate = useNavigate();
  const location = useLocation();
  const qs = new URLSearchParams(location.search);
  const hashId = qs.get("id");
  let histId = null;
  try { histId = hashId ? Number(decryptText(decodeURIComponent(hashId))) : null; }
  catch { histId = null; }

  const [h, setH] = useState(null);
  const [loading, setLoading] = useState(true);
  const [resending, setResending] = useState({});

  const cargar = async () => {
    if (!histId) { setLoading(false); return; }
    setLoading(true);
    try {
      const data = await getHistorialCobranzaDetalle(histId);
      setH(data);
    } catch (e) {
      toast.error("Error al cargar detalle del corte.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { cargar(); }, [histId]);

  const onResend = async (detalleId) => {
    setResending((s) => ({ ...s, [detalleId]: true }));
    try {
      const r = await reintentarEmailDetalle(detalleId);
      if (r?.estado === "ok" && r?.exito) {
        toast.success("Correo reenviado.");
        cargar();
      } else {
        toast.warning(r?.mensaje || "Fallo el reenvío.");
      }
    } finally {
      setResending((s) => ({ ...s, [detalleId]: false }));
    }
  };

  if (loading) return <div className="p-12 text-center text-blueGray-400">Cargando...</div>;
  if (!h) return <div className="p-12 text-center text-blueGray-400">Corte no encontrado.</div>;

  return (
    <div className="mx-auto w-full">
      <ToastContainer />
      <div className="bg-white rounded-lg shadow-lg mb-4">
        <div className="px-6 py-6 flex justify-between items-center border-b flex-wrap gap-3">
          <div>
            <h6 className="text-blueGray-700 text-xl font-bold capitalize">
              Corte de cobranza — {fmtPeriodo(h.periodo)}
            </h6>
            <p className="text-xs text-blueGray-500 mt-1">
              Ejecutado por <strong>{h.usuario?.nombre || h.usuario?.email || "—"}</strong>
              {h.fecha_calculo ? ` el ${(h.fecha_calculo).split("T")[0]}` : ""}
            </p>
          </div>
          <button
            onClick={() => navigate("/billing-history")}
            className="text-blueGray-500 hover:text-blueGray-700 text-sm"
          >
            <i className="fas fa-arrow-left mr-1"></i> Volver al historial
          </button>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-6">
          <div className="bg-blueGray-50 rounded-lg p-3">
            <p className="text-xs text-blueGray-500 uppercase font-bold">Empresas</p>
            <p className="text-2xl font-bold text-blueGray-700">{h.nro_empresa_recaudadas}</p>
          </div>
          <div className="bg-violet-50 rounded-lg p-3">
            <p className="text-xs text-violet-700 uppercase font-bold">Facturas</p>
            <p className="text-2xl font-bold text-violet-700">{h.nro_facturas_acumulada ?? 0}</p>
          </div>
          <div className="bg-emerald-50 rounded-lg p-3">
            <p className="text-xs text-emerald-700 uppercase font-bold">Monto Bs.</p>
            <p className="text-2xl font-bold text-emerald-700">{fmtMonto(h.monto_acumulado)}</p>
          </div>
          <div className="bg-sky-50 rounded-lg p-3">
            <p className="text-xs text-sky-700 uppercase font-bold">Monto USD*</p>
            <p className="text-2xl font-bold text-sky-700">{fmtMonto(h.monto_acumulado_aplicada)}</p>
          </div>
          <div className="bg-indigo-50 rounded-lg p-3">
            <p className="text-xs text-indigo-700 uppercase font-bold">Tasa BCV</p>
            <p className="text-2xl font-bold text-indigo-700">{h.tasa_bcv_aplicada ? fmtMonto(h.tasa_bcv_aplicada) : "—"}</p>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b">
          <h6 className="text-blueGray-700 text-lg font-bold">Detalle por empresa</h6>
        </div>
        <div className="overflow-x-auto p-4">
          <table className="w-full text-sm">
            <thead className="bg-blueGray-50">
              <tr className="text-blueGray-600">
                <th className="text-left py-2 px-3">RIF</th>
                <th className="text-left py-2 px-3">Razón social</th>
                <th className="text-right py-2 px-3">Volumen</th>
                <th className="text-left py-2 px-3">Regla</th>
                <th className="text-right py-2 px-3">Tarifa hist.</th>
                <th className="text-right py-2 px-3">Monto Bs.</th>
                <th className="text-right py-2 px-3">Monto orig.</th>
                <th className="text-center py-2 px-3">Notif.</th>
                <th className="text-center py-2 px-3">Acción</th>
              </tr>
            </thead>
            <tbody>
              {(h.detalles || []).map((d) => (
                <tr key={d.id} className="border-t hover:bg-blueGray-50/50">
                  <td className="py-2 px-3 font-mono text-xs">{d.empresa?.rif || "—"}</td>
                  <td className="py-2 px-3">{d.empresa?.nombre_empresa || "—"}</td>
                  <td className="py-2 px-3 text-right font-bold">{d.nro_factura}</td>
                  <td className="py-2 px-3">
                    {d.regla?.nombre || "—"}
                    <div className="text-xs text-blueGray-400">
                      {d.regla_tipo === "lote" ? "lote" : "por factura"}
                    </div>
                  </td>
                  <td className="py-2 px-3 text-right text-xs">
                    {fmtMonto(d.regla_monto)} {d.regla_moneda?.codigo || ""}
                  </td>
                  <td className="py-2 px-3 text-right font-bold">{fmtMonto(d.monto)}</td>
                  <td className="py-2 px-3 text-right">{fmtMonto(d.monto_aplicada)}</td>
                  <td className="py-2 px-3 text-center">
                    {d.notificacion_envio ? (
                      <span className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-semibold bg-emerald-100 text-emerald-700"><i className="fas fa-check-circle"></i> Enviado</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 py-0.5 px-2 rounded-full text-xs font-semibold bg-amber-100 text-amber-700"><i className="fas fa-clock"></i> Pendiente</span>
                    )}
                  </td>
                  <td className="py-2 px-3 text-center">
                    {!d.notificacion_envio && (
                      <button
                        onClick={() => onResend(d.id)}
                        disabled={!!resending[d.id]}
                        title="Reenviar correo"
                        className="text-amber-600 hover:text-amber-800 px-2 disabled:opacity-50"
                      >
                        {resending[d.id]
                          ? <i className="fas fa-spinner fa-spin"></i>
                          : <i className="fas fa-paper-plane"></i>}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
